// CTV-840: grab the frame a <video> is paused on, as an image.
//
// The video must be served with CORS (the API signs the station's clips from
// Supabase storage, which sends the header) and the element must carry
// crossOrigin="anonymous", or the canvas is tainted and toBlob throws. Frames
// are capped at FRAME_MAX_WIDTH: the TV screen in the social square is about
// 1800px wide at the 4x export, and station footage is 1080p, so 1920 keeps
// the still sharp without upscaling and the upload under a megabyte or two.

export const FRAME_MAX_WIDTH = 1920;
export const FRAME_MIME = 'image/jpeg';
const FRAME_QUALITY = 0.9;

/** The current frame as a JPEG blob. Rejects when the frame cannot be read. */
export function captureVideoFrame(video: HTMLVideoElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return reject(new Error('The clip has not loaded yet'));
    const scale = Math.min(1, FRAME_MAX_WIDTH / w);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('No canvas'));
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the frame'))),
        FRAME_MIME,
        FRAME_QUALITY,
      );
    } catch (e) {
      reject(e instanceof Error ? e : new Error('Could not read the frame'));
    }
  });
}

/**
 * An uploaded image (JPEG or PNG) re-encoded like a captured frame: scaled
 * down to FRAME_MAX_WIDTH and saved as a JPEG, so a 20MB camera photo lands
 * as the same kind of still a clip frame does.
 */
export function captureImageFile(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return reject(new Error('Could not read the image'));
      const scale = Math.min(1, FRAME_MAX_WIDTH / w);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No canvas'));
      // A PNG with transparency gets a black screen behind it, not white.
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image'))),
        FRAME_MIME,
        FRAME_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image'));
    };
    img.src = url;
  });
}

/** A data URL for showing the frame in the live preview. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the frame'));
    reader.readAsDataURL(blob);
  });
}
